import { isSelectableSalesModel } from '../../src/sales-model-policy';
import type { DraftItem, ModelOption } from './types';

const SEARCH_PUNCTUATION = /[\s\-_.\/\\'"“”‘’()（）]+/g;

export function normalizeModelText(value: string): string {
  return value.normalize('NFKC').toUpperCase().replace(SEARCH_PUNCTUATION, '');
}

function rankModel(model: ModelOption, query: string): number {
  const name = normalizeModelText(model.name);
  const id = normalizeModelText(model.model_id);
  const aliases = (model.aliases || []).map(normalizeModelText);
  if (name === query || id === query) return 0;
  if (aliases.some(alias => alias === query)) return 1;
  if (name.startsWith(query) || id.startsWith(query)) return 2;
  if (aliases.some(alias => alias.startsWith(query))) return 3;
  if (name.includes(query) || id.includes(query)) return 4;
  if (aliases.some(alias => alias.includes(query))) return 5;
  return Number.POSITIVE_INFINITY;
}

export function searchModels(models: ModelOption[], rawQuery: string, limit = 30): ModelOption[] {
  const query = normalizeModelText(rawQuery);
  if (query.length < 4) return [];
  return models
    .filter(isSelectableSalesModel)
    .map(model => ({ model, rank: rankModel(model, query) }))
    .filter(candidate => Number.isFinite(candidate.rank))
    .sort((left, right) => left.rank - right.rank || left.model.name.localeCompare(right.model.name))
    .slice(0, limit)
    .map(candidate => candidate.model);
}

export function modelMatchesSelection(model: ModelOption, value: string): boolean {
  if (!isSelectableSalesModel(model)) return false;
  const normalized = normalizeModelText(value);
  return normalizeModelText(model.name) === normalized
    || normalizeModelText(model.model_id) === normalized
    || (model.aliases || []).some(alias => normalizeModelText(alias) === normalized);
}

export function selectableSalesModels(models: ModelOption[]): ModelOption[] {
  return models.filter(isSelectableSalesModel);
}

export function reconcileDraftItemsWithCatalog(
  items: DraftItem[],
  models: ModelOption[]
): { items: DraftItem[]; clearedLabels: string[] } {
  const selectableIds = new Set(selectableSalesModels(models).map(model => model.model_id));
  const clearedLabels: string[] = [];
  let changed = false;
  const nextItems = items.map(item => {
    if (!item.model_id || selectableIds.has(item.model_id)) return item;
    changed = true;
    clearedLabels.push(item.model_text.trim() || item.model_id);
    return { ...item, model_id: '', model_text: '' };
  });
  return { items: changed ? nextItems : items, clearedLabels };
}
